import { useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
const THROTTLE_MS = 30_000; // only reset timer every 30s of activity

/**
 * HIPAA-compliant idle timeout hook.
 * Calls `onTimeout` after 15 minutes of inactivity.
 */
export function useIdleTimeout(onTimeout: () => void, enabled = true) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastResetRef = useRef<number>(Date.now());

    const resetTimer = useCallback(() => {
        const now = Date.now();
        if (now - lastResetRef.current < THROTTLE_MS) return;
        lastResetRef.current = now;

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(onTimeout, IDLE_TIMEOUT_MS);
    }, [onTimeout]);

    useEffect(() => {
        if (!enabled) return;

        // Start the initial timer
        timerRef.current = setTimeout(onTimeout, IDLE_TIMEOUT_MS);

        for (const event of ACTIVITY_EVENTS) {
            window.addEventListener(event, resetTimer, { passive: true });
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            for (const event of ACTIVITY_EVENTS) {
                window.removeEventListener(event, resetTimer);
            }
        };
    }, [enabled, onTimeout, resetTimer]);
}
